import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { Button, TextField, FormControlLabel, Switch, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import Venue from 'types/models/Venue';
import { StockWarehouse, StockWarehousePayload, StockWarehouseType } from 'types/models/StockReceipt';
import { WarehouseFormSchema } from 'forms/schemas/StockModuleForm';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import { getVenueWarehouses, storeStockWarehouse, updateStockWarehouse } from 'services/stockWarehouseServices';
import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import User from 'types/models/User';
import AppContext from 'contexts/AppContext';
import SaveError from 'components/common/SaveError';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';
import { getUsersByEditorType } from 'services/userServices';

type Props = {
  warehouse?: StockWarehouse;
};

export default ({ warehouse }: Props): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [managerUsers, setManagerUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [hasCentralWarehouse, setHasCentralWarehouse] = React.useState<boolean>(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(!loading);

  React.useEffect(() => {
    async function loadManagers() {
      const managers = await getUsersByEditorType(UserRoleType.WarehouseEditor);
      setManagerUsers(managers ?? []);
    }

    if (isVenueEditor) {
      setLoading(false);
      loadManagers();
      return;
    }

    async function load() {
      try {
        const data = await getSimpleVenuesWithStockModuleActive();
        setVenues(data ?? []);
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
    loadManagers();
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueId: warehouse?.venueId ?? entity?.id ?? '',
      name: warehouse?.name ?? '',
      type: warehouse?.type ?? StockWarehouseType.CENTRAL,
      managerUserId: warehouse?.managerUserId ?? '',
      isActive: warehouse?.isActive ?? true,
    },
    validationSchema: WarehouseFormSchema(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  React.useEffect(() => {
    const venueId = formik.values.venueId;
    if (!venueId) {
      setHasCentralWarehouse(false);
      return;
    }

    let cancelled = false;
    setHasCentralWarehouse(false);

    getVenueWarehouses(venueId)
      .then((warehouses) => {
        if (cancelled) return;
        const hasCentral = warehouses.some((warehouseItem) => warehouseItem.type === StockWarehouseType.CENTRAL);
        setHasCentralWarehouse(hasCentral);
        if (hasCentral && !warehouse) {
          formik.setFieldValue('type', StockWarehouseType.INTERMEDIATE);
        }
      })
      .catch(() => {
        if (!cancelled) setHasCentralWarehouse(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formik.values.venueId]);

  async function onConfirm() {
    setSaveError(null);
    const payload: StockWarehousePayload = {
      venueId: formik.values.venueId,
      name: formik.values.name,
      type: formik.values.type,
      managerUserId: formik.values.managerUserId || null,
      isActive: formik.values.isActive,
    };

    formik.setSubmitting(true);

    try {
      if (warehouse) {
        await updateStockWarehouse(warehouse.id, payload);
      } else {
        await storeStockWarehouse(payload);
      }
      history.go(-1);
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      formik.setSubmitting(false);
      setOpenDialog(false);
    }
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.warehouses.form.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
            onBlur={formik.handleBlur}
          />
        )}

        <TextField
          fullWidth
          margin="normal"
          name="name"
          label={t('stock.warehouses.form.name')}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        <SelectField
          name="type"
          label={t('stock.warehouses.form.type')}
          placeholder={t('stock.warehouses.form.type-placeholder')}
          value={formik.values.type}
          options={
            hasCentralWarehouse
              ? [
                  {
                    label: t(`stock.warehouse-types.${formik.values.type}`),
                    value: formik.values.type,
                  },
                ]
              : Object.values(StockWarehouseType).map((type) => ({
                  label: t(`stock.warehouse-types.${type}`),
                  value: type,
                }))
          }
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.type}
          error={formik.errors.type}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting || hasCentralWarehouse}
          isSearchable={false}
          onBlur={formik.handleBlur}
        />

        <SelectField
          name="managerUserId"
          label={t('stock.warehouses.form.manager')}
          placeholder={t('stock.warehouses.form.manager-placeholder')}
          value={formik.values.managerUserId}
          options={mapToSelectOptions(managerUsers, { label: 'name' })}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.managerUserId}
          error={formik.errors.managerUserId}
          isClearable
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable
          onBlur={formik.handleBlur}
        />

        <FormControlLabel
          control={<Switch checked={formik.values.isActive} onChange={formik.handleChange} name="isActive" />}
          label={t('stock.warehouses.form.active')}
        />

        <SaveError error={saveError} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            marginTop: 24,
          }}
        >
          <Button variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {warehouse ? t('stock.warehouses.form.buttons.save') : t('stock.warehouses.form.buttons.create')}
          </Button>
        </div>
      </form>
    </>
  );
};
